import * as React from "react"
import { X } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import type { CreateGapClientRequest } from "@/types/gap-client"
import { useNumericInput, type NumericInputValue } from "@/hooks/useNumericInput"

interface AddClientFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateGapClientRequest) => Promise<void>
  isSubmitting?: boolean
}

interface FormData {
  clientId: string
  venueId: NumericInputValue
  necThreshold: NumericInputValue
  userInstructionsEnabled: boolean
  adminPassword: string
}

interface FormErrors {
  clientId?: string
  adminPassword?: string
}

const initialFormData: FormData = {
  clientId: "",
  venueId: "",
  necThreshold: "",
  userInstructionsEnabled: false,
  adminPassword: "",
}

export function AddClientForm({ open, onOpenChange, onSubmit, isSubmitting = false }: AddClientFormProps) {
  const [formData, setFormData] = React.useState<FormData>(initialFormData)
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})
  const firstInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (open) {
      setFormData(initialFormData)
      setErrors({})
      setTouched({})
      setTimeout(() => firstInputRef.current?.focus(), 100)
    }
  }, [open])

  const venueIdInput = useNumericInput({
    min: 1,
    integer: true,
    value: formData.venueId,
    validate: (value) => {
      if (value === "") return "Venue ID must be a valid positive integer"
      if (!Number.isInteger(value) || value < 1) {
        return "Venue ID must be a valid positive integer"
      }
      return undefined
    },
  })

  const necThresholdInput = useNumericInput({
    min: 0,
    max: 10000,
    integer: true,
    value: formData.necThreshold,
    validate: (value) => {
      if (value === "") return "NEC Match Threshold must be a valid number between 0 and 10000"
      if (!Number.isInteger(value) || value < 0 || value > 10000) {
        return "NEC Match Threshold must be a valid number between 0 and 10000"
      }
      return undefined
    },
  })

  const validateField = (name: "clientId" | "adminPassword", value: string): string | undefined => {
    switch (name) {
      case "clientId":
        if (!value.trim()) return "Client ID is required"
        if (!/^[A-Za-z0-9-]+$/.test(value)) return "Client ID can only contain letters, numbers, and hyphens"
        return undefined
      case "adminPassword":
        if (!value.trim()) return "Admin Password is required"
        if (value.length < 8) return "Password must be at least 8 characters"
        return undefined
      default:
        return undefined
    }
  }

  const handleBlur = (name: "clientId" | "adminPassword") => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    const error = validateField(name, formData[name] as string)
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  const handleChange = (name: keyof FormData, value: string | boolean | NumericInputValue) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (name === "clientId" && touched.clientId) {
      const error = validateField("clientId", value as string)
      setErrors((prev) => ({ ...prev, clientId: error }))
    } else if (name === "adminPassword" && touched.adminPassword) {
      const error = validateField("adminPassword", value as string)
      setErrors((prev) => ({ ...prev, adminPassword: error }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    const fields: Array<"clientId" | "adminPassword"> = ["clientId", "adminPassword"]

    fields.forEach((field) => {
      const error = validateField(field, formData[field] as string)
      if (error) newErrors[field] = error
    })

    setErrors(newErrors)
    setTouched({ clientId: true, adminPassword: true })
    return Object.keys(newErrors).length === 0
  }

  const canSubmit =
    !venueIdInput.error &&
    !necThresholdInput.error &&
    formData.venueId !== "" &&
    formData.necThreshold !== "" &&
    !errors.clientId &&
    !errors.adminPassword

  const handleSubmit = async () => {
    if (!validateForm()) return
    if (
      venueIdInput.error ||
      necThresholdInput.error ||
      formData.venueId === "" ||
      formData.necThreshold === ""
    ) {
      setTouched((prev) => ({ ...prev, venueId: true, necThreshold: true }))
      return
    }

    const createData: CreateGapClientRequest = {
      clientId: formData.clientId,
      venueId: formData.venueId as number,
      necMatchScoreThreshold: Math.round(formData.necThreshold as number),
      featureFlagUserInstructions: formData.userInstructionsEnabled,
      adminPassword: formData.adminPassword,
    }

    await onSubmit(createData)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[540px] sm:max-w-[540px] bg-card border-l border-border p-0 flex flex-col [&>button]:hidden"
      >
        {/* Header */}
        <SheetHeader className="px-6 py-5 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-lg font-semibold text-foreground">Add New Client</SheetTitle>
              <SheetDescription className="text-muted-foreground mt-1">
                Configure a new client for the system
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground hover:bg-secondary -mr-2 -mt-1"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </SheetHeader>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Client Identification Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Client Identification
              </h3>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Client ID */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Client ID <span className="text-destructive">*</span>
              </Label>
              <Input
                ref={firstInputRef}
                value={formData.clientId}
                onChange={(e) => handleChange("clientId", e.target.value)}
                onBlur={() => handleBlur("clientId")}
                placeholder="e.g., acme-corp"
                className={`font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.clientId && touched.clientId ? "border-destructive" : ""
                }`}
              />
              {errors.clientId && touched.clientId && <p className="text-xs text-destructive">{errors.clientId}</p>}
            </div>

            {/* Venue ID */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Venue ID <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min={1}
                value={venueIdInput.inputValue}
                onChange={(e) => {
                  venueIdInput.handleChange(e)
                  const nextRaw = e.target.value
                  let nextValue: NumericInputValue = ""
                  if (nextRaw !== "") {
                    const parsed = Number(nextRaw)
                    nextValue = Number.isNaN(parsed) ? "" : parsed
                  }
                  setFormData((prev) => ({ ...prev, venueId: nextValue }))
                }}
                onBlur={() => {
                  venueIdInput.handleBlur()
                  setTouched((prev) => ({ ...prev, venueId: true }))
                }}
                placeholder="e.g., 1"
                className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  touched.venueId && venueIdInput.error ? "border-destructive" : ""
                }`}
              />
              {touched.venueId && venueIdInput.error && (
                <p className="text-xs text-destructive">{venueIdInput.error}</p>
              )}
            </div>
          </div>

          {/* Configuration Settings Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Configuration Settings
              </h3>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* NEC Match Score Threshold */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                NEC Match Score Threshold <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min={0}
                max={10000}
                step={1}
                value={necThresholdInput.inputValue}
                onChange={(e) => {
                  necThresholdInput.handleChange(e)
                  const nextRaw = e.target.value
                  let nextValue: NumericInputValue = ""
                  if (nextRaw !== "") {
                    const parsed = Number(nextRaw)
                    nextValue = Number.isNaN(parsed) ? "" : parsed
                  }
                  setFormData((prev) => ({ ...prev, necThreshold: nextValue }))
                }}
                onBlur={() => {
                  necThresholdInput.handleBlur()
                  setTouched((prev) => ({ ...prev, necThreshold: true }))
                }}
                placeholder="e.g., 8500"
                autoComplete="off"
                className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  touched.necThreshold && necThresholdInput.error ? "border-destructive" : ""
                }`}
              />
              {touched.necThreshold && necThresholdInput.error ? (
                <p className="text-xs text-destructive">{necThresholdInput.error}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Enter an integer between 0 and 10000</p>
              )}
            </div>

            {/* User Instructions Toggle */}
            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label className="text-[13px] text-muted-foreground font-medium">User Instructions</Label>
                <p className="text-xs text-muted-foreground">Enable to show user instructions on devices</p>
              </div>
              <Switch
                checked={formData.userInstructionsEnabled}
                onCheckedChange={(checked) => handleChange("userInstructionsEnabled", checked)}
                className="data-[state=checked]:bg-primary"
              />
            </div>
          </div>

          {/* Security Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Security</h3>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Admin Password */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Admin Password <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                value={formData.adminPassword}
                onChange={(e) => handleChange("adminPassword", e.target.value)}
                onBlur={() => handleBlur("adminPassword")}
                placeholder="Enter admin password"
                autoComplete="off"
                className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.adminPassword && touched.adminPassword ? "border-destructive" : ""
                }`}
              />
              {errors.adminPassword && touched.adminPassword && (
                <p className="text-xs text-destructive">{errors.adminPassword}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isSubmitting && <LoadingSpinner size="sm" className="mr-2" />}
            {isSubmitting ? "Creating..." : "Create Client"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
